import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { login } from "../../store/actions/authActions";
import { verify2FA } from "../../store/actions/authActions";
import { connect } from "react-redux";
import { compose } from "redux";
import { Link } from "react-router-dom";
import { Redirect } from 'react-router-dom';

import Avatar from '../../assets/images/profile.png';
import LOADER from "../../assets/images/loader.svg";

import dashboardImg1 from '../../assets/images/Login1.png';
import dashboardImg2 from '../../assets/images/Login2.png';
import dashboardImg3 from '../../assets/images/Login3.png';
import dashboardImg4 from '../../assets/images/Login4.png';

/** Same key as TopBar / i18n.js */
const LANG_STORAGE_KEY = "rabo_i18n_language";

function Login(props) {
  const { t, i18n } = useTranslation();
  const { token, loginFN, errore, verify2FAFN } = props;

  const [show2FA, setShow2FA] = useState(false);
  const [username, setusername] = useState(null);
  const [pwd, setpwd] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setloading] = useState(false);
  const [code, setCode] = useState(null);
  const [codeDigits, setCodeDigits] = useState(['', '', '', '', '', '']);
  const [featureIndex, setFeatureIndex] = useState(0);

  const setLanguage = (lng) => {
    i18n.changeLanguage(lng);
    localStorage.setItem(LANG_STORAGE_KEY, lng);
  };

  const activeLang = i18n.resolvedLanguage || i18n.language || "en";
  const isEn = activeLang === "en" || activeLang.startsWith("en");
  const isTr = activeLang === "tr" || activeLang.startsWith("tr");

  const slides = useMemo(
    () => [
      {
        title: t("loginPage.slides.control.title"),
        description: t("loginPage.slides.control.description"),
        image: dashboardImg2,
        icon: '🛡️',
      },
      {
        title: t("loginPage.slides.ai.title"),
        description: t("loginPage.slides.ai.description"),
        image: dashboardImg1,
        icon: '✨',
      },
      {
        title: t("loginPage.slides.workflow.title"),
        description: t("loginPage.slides.workflow.description"),
        image: dashboardImg3,
        icon: '⚡',
      },
      {
        title: t("loginPage.slides.compliance.title"),
        description: t("loginPage.slides.compliance.description"),
        image: dashboardImg4,
        icon: '✅',
      },
    ],
    [t]
  );

  useEffect(() => {
    if (token || errore) {
      setloading(false);
    }
  }, [token, errore]);

  useEffect(() => {
    if (show2FA) {
      setloading(false);
    }
  }, [show2FA]);

  useEffect(() => {
    if (!slides.length) return undefined;
    const interval = setInterval(() => {
      setFeatureIndex((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [slides.length]);

  const handleCodeDigitChange = (index, value) => {
    if (value.length > 1) return;

    const newDigits = [...codeDigits];
    newDigits[index] = value;
    setCodeDigits(newDigits);
    setCode(newDigits.join(''));

    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !codeDigits[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  const handle2FAVerify = (e) => {
    e.preventDefault();
    setloading(true);
    const fullCode = codeDigits.join('');
    verify2FAFN(username, fullCode);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setloading(true);
    loginFN(
      username,
      pwd,
      () => setShow2FA(true)
    );
  };

  const handleCodePaste = (e) => {
    e.preventDefault();

    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;

    const newDigits = [...codeDigits];
    for (let i = 0; i < 6; i++) {
      newDigits[i] = pasted[i] || "";
    }

    setCodeDigits(newDigits);

    const nextIndex = Math.min(pasted.length, 5);
    setTimeout(() => {
      document.getElementById(`code-${nextIndex}`)?.focus();
    }, 0);
  };

  const languageSwitcher = (
    <div className="fixed top-5 right-5 z-[100] flex items-center gap-1 text-xs text-[#3b3b3b] bg-white/95 px-2 py-1.5 rounded-md border border-gray-200 shadow-sm">
      <button
        type="button"
        onClick={() => setLanguage("en")}
        aria-pressed={isEn}
        className={`px-1.5 rounded hover:bg-gray-100 ${isEn ? "font-bold" : ""}`}
      >
        EN
      </button>
      <span className="text-gray-400 select-none" aria-hidden>|</span>
      <button
        type="button"
        onClick={() => setLanguage("tr")}
        aria-pressed={isTr}
        className={`px-1.5 rounded hover:bg-gray-100 ${isTr ? "font-bold" : ""}`}
      >
        TR
      </button>
    </div>
  );

  const renderAuthRightPanel = () => (
    <div className="hidden md:flex flex-col items-center justify-center text-center px-10 text-white bg-gradient-to-br from-indigo-500 to-indigo-700">
      <h2 className="text-3xl font-semibold mb-4">
        {slides[featureIndex].title}
      </h2>

      <p className="max-w-[420px] text-white/90 mb-10 leading-relaxed">
        {slides[featureIndex].description}
      </p>

      <div className="bg-white rounded-xl p-4 shadow-xl w-full max-w-[480px]">
        <img
          src={slides[featureIndex].image}
          alt={t("loginPage.sliderImageAlt")}
          className="rounded-lg w-full"
        />
      </div>

      <div className="flex gap-2 mt-6">
        {slides.map((_, i) => (
          <div
            key={i}
            className={`h-2 rounded-full transition-all ${
              i === featureIndex ? "w-6 bg-white" : "w-2 bg-white/40"
            }`}
          />
        ))}
      </div>
    </div>
  );

  if (token) {
    return <Redirect to="/risk" />;
  }

  if (show2FA) {
    return (
      <>
        {languageSwitcher}
        <div className="min-h-screen bg-white flex items-center justify-center p-6">
          <div className="w-full max-w-[1200px] md:h-[720px] bg-white rounded-[22px] overflow-hidden grid grid-cols-1 md:grid-cols-2 shadow-lg">

            <div className="flex items-center justify-center px-6 md:px-12 py-10 md:py-0">
              <div className="w-full max-w-[380px]">

                <button
                  type="button"
                  onClick={() => setShow2FA(false)}
                  className="flex items-center gap-2 text-gray-500 hover:text-gray-800 mb-6 transition-colors"
                >
                  <i className="fas fa-arrow-left"></i>
                  <span>{t("loginPage.twoFactor.back")}</span>
                </button>

                <h1 className="text-[34px] font-semibold mb-2">
                  {t("loginPage.twoFactor.title")}
                </h1>

                <p className="text-gray-500 text-base mb-1">
                  {t("loginPage.twoFactor.codeSentIntro")}
                </p>

                <p className="text-gray-700 text-base font-medium mb-8 break-all">
                  {username}
                </p>

                <form onSubmit={handle2FAVerify}>
                  <p className="text-gray-700 mb-4 text-sm" aria-hidden="true">
                    {t("loginPage.twoFactor.enterCode")}
                  </p>

                  <fieldset className="border-0 p-0 m-0">
                    <legend className="sr-only">{t("loginPage.twoFactor.enterCode")}</legend>
                    <div className="flex gap-2 justify-center mb-6">
                      {codeDigits.map((digit, index) => (
                        <input
                          key={index}
                          id={`code-${index}`}
                          aria-label={`${index + 1} / 6`}
                          type="text"
                          inputMode="numeric"
                          onPaste={handleCodePaste}
                          pattern="[0-9]*"
                          value={digit}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^0-9]/g, "");
                            handleCodeDigitChange(index, value);
                          }}
                          onKeyDown={(e) => handleCodeKeyDown(index, e)}
                          maxLength={1}
                          required
                          className={`w-12 h-16 text-center text-lg font-semibold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                            errore ? "border-red-500" : "border-gray-300"
                          }`}
                        />
                      ))}
                    </div>
                  </fieldset>

                  {errore && (
                    <p role="alert" className="text-red-500 mt-3 text-center text-sm">
                      {errore}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={loading || codeDigits.join("").length !== 6}
                    aria-busy={loading}
                    aria-label={loading ? t("loginPage.verifying") : t("loginPage.twoFactor.verifyCode")}
                    className="w-full h-[46px] rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-base font-semibold transition flex items-center justify-center gap-2 mb-6"
                  >
                    {loading ? (
                      <img src={LOADER} alt="" aria-hidden="true" className="w-4 h-4 animate-spin" />
                    ) : (
                      t("loginPage.twoFactor.verifyCode")
                    )}
                  </button>
                </form>

                <p className="text-center text-gray-500 text-sm">
                  {t("loginPage.twoFactor.didntReceive")}{" "}
                  <button type="button" className="text-indigo-600 hover:underline">
                    {t("loginPage.twoFactor.resend")}
                  </button>
                </p>

              </div>
            </div>

            {renderAuthRightPanel()}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {languageSwitcher}
      <div className="min-h-screen bg-white flex items-center justify-center p-6">
        <div className="w-full max-w-[1200px] md:h-[720px] bg-white rounded-[22px] overflow-hidden grid grid-cols-1 md:grid-cols-2 shadow-lg">

          <div className="flex items-center justify-center bg-white px-6 md:px-12 py-10 md:py-0">
            <div className="w-full max-w-[380px]">

              <h1 className="text-[34px] font-semibold mb-1">
                {t("loginPage.welcomeBack")}
              </h1>

              <p className="text-gray-500 text-base mb-8">
                {t("loginPage.subtitle")}
              </p>

              <form onSubmit={handleSubmit}>

                <div className="mb-5">
                  <label htmlFor="username" className="block text-sm font-semibold mb-2">
                    {t("loginPage.email")}
                  </label>

                  <input
                    required
                    type="email"
                    placeholder={t("loginPage.placeholders.email")}
                    id="username"
                    name="username"
                    autoComplete="username"
                    onChange={(v) => setusername(v.target.value)}
                    value={username || ""}
                    className="w-full h-[44px] px-4 rounded-lg bg-gray-100 border border-gray-200 focus:outline-none focus:border-indigo-500"
                  />
                </div>

                <div className="mb-4">
                  <label htmlFor="pwd" className="block text-sm font-semibold mb-2">
                    {t("loginPage.password")}
                  </label>

                  <div className="relative">
                    <input
                      required
                      type={showPassword ? "text" : "password"}
                      placeholder={t("loginPage.placeholders.password")}
                      id="pwd"
                      name="pwd"
                      onChange={(v) => setpwd(v.target.value)}
                      value={pwd || ""}
                      autoComplete="current-password"
                      className="w-full h-[44px] px-4 pr-10 rounded-lg bg-gray-100 border border-gray-200 focus:outline-none focus:border-indigo-500"
                    />

                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-200/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
                      aria-label={showPassword ? t("loginPage.hidePassword") : t("loginPage.showPassword")}
                    >
                      <i className={showPassword ? "far fa-eye-slash" : "far fa-eye"} aria-hidden />
                    </button>
                  </div>
                </div>

                <div className="flex justify-between items-center mb-6 text-sm">
                  <label className="flex items-center gap-2 text-gray-600">
                    <input type="checkbox" className="accent-indigo-600" />
                    {t("loginPage.rememberMe")}
                  </label>

                  <Link
                    to="/recovery"
                    className="text-indigo-600 hover:underline"
                  >
                    {t("loginPage.forgotPassword")}
                  </Link>
                </div>

                {errore && (
                  <div role="alert" className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm text-center">
                    {errore}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  aria-busy={loading}
                  aria-label={loading ? t("loginPage.signingIn") : t("loginPage.signIn")}
                  className="w-full h-[46px] rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-base font-semibold transition flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <img src={LOADER} alt="" aria-hidden="true" className="w-4 h-4 animate-spin" />
                  ) : (
                    t("loginPage.signIn")
                  )}
                </button>

                <div className="flex items-center gap-4 my-7">
                  <div className="flex-1 h-px bg-gray-300" />
                  <span className="text-gray-500 text-sm">{t("loginPage.orContinueWith")}</span>
                  <div className="flex-1 h-px bg-gray-300" />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    disabled
                    title={t("loginPage.socialComingSoon")}
                    className="h-[44px] border rounded-lg flex items-center justify-center gap-2 opacity-50 cursor-not-allowed"
                  >
                    <i className="fab fa-google" aria-hidden="true" />
                    {t("loginPage.socialGoogle")}
                  </button>

                  <button
                    type="button"
                    disabled
                    title={t("loginPage.socialComingSoon")}
                    className="h-[44px] border rounded-lg flex items-center justify-center gap-2 opacity-50 cursor-not-allowed"
                  >
                    <i className="fab fa-microsoft" aria-hidden="true" />
                    {t("loginPage.socialMicrosoft")}
                  </button>
                </div>

                <p className="text-center text-gray-500 text-sm mt-6">
                  {t("loginPage.noAccount")}{" "}
                  <button type="button" className="text-indigo-600 hover:underline font-medium">
                    {t("loginPage.signUp")}
                  </button>
                </p>

              </form>
            </div>
          </div>

          {renderAuthRightPanel()}
        </div>
      </div>
    </>
  );
}

const mapStateToProps = (state) => {
  return {
    errore: state.auth.errore,
    token: state.auth.token,

  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    loginFN: (username, pwd, callback) =>
      dispatch(login(username, pwd, callback)),
    verify2FAFN: (username, code) =>
      dispatch(verify2FA(username, code)),
  };
};

export default compose(connect(mapStateToProps, mapDispatchToProps))(Login);